import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { MoreVertical, Phone, MessageCircle, Loader, Check } from 'lucide-react';
import BackButtonIconPng from '../../../../assets/Icons/SingleAlbum/back.png';

import {
  getLeadDetailAPI,
  updateLeadAPI,
  updateLeadStageAPI,
  triggerFollowUpAPI,
  pauseFollowUpAPI,
  logLeadNoteAPI,
  listLeadActivitiesAPI,
  deleteLeadAPI,
} from '../../../../Redux/ApiCalls/Dashboard/LeadsAPI';
import {
  STAGE_ORDER,
  STAGE_LABELS,
  VALID_TRANSITIONS,
  EVENT_TYPE_LABELS,
} from '../../../../Data/leads.dto';
import { useAppSelector } from '../../../../Redux/Hooks';
import type {
  LeadDetail,
  LeadActivity,
  LeadStage,
  AIPriority,
} from '../../../../Data/leads.dto';
import { showSuccessToast, showErrorToast } from '../../../atoms/Utlis/Toast';
import { useWhatsApp } from '../../../../hooks/useWhatsApp';
import { usePermissions } from '../../../../hooks/usePermissions';
import PermissionGate from '../../../atoms/Utlis/PermissionGate';
import MarkLostModal from './MarkLostModal';
import AddLeadModal from './AddLeadModal';
import LeadActivityItem from '../../../molecules/Dashboard/Leads/LeadActivityItem';

import {
  formatBudget,
  formatEventDate,
  formatScheduledAt,
  PRIORITY_BG,
} from './leadHelpers';


import { useDocumentSharing } from './useDocumentSharing';
import { NoDocumentModal } from './NoDocumentModal';
import { VerifyDocumentModal } from './VerifyDocumentModal';
import { SelectDocumentModal } from './SelectDocumentModal';
import { DocumentPrintTemplate } from '../DocumentPrintTemplate';
import { countryCodes } from '../../../../Utils/countryCodes';

const formatPhoneNumber = (phone: string, countryCode?: string | null) => {
  if (!phone) return '';
  if (!countryCode) return phone;
  return `${countryCode} ${phone}`;
};

const getFullPhone = (phone: string, countryCode?: string | null) => {
  if (!phone) return '';
  if (!countryCode) return phone;
  return `${countryCode}${phone}`;
};





const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #374151;
  padding: 4px;
  &:hover { color: #111; }
`;

const LeadName = styled.h2`
  font-family: 'Urbanist', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #111827;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const MenuBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #6b7280;
  padding: 4px;
  position: relative;
`;

const DropMenu = styled.div<{ open: boolean }>`
  display: ${p => p.open ? 'block' : 'none'};
  position: absolute;
  top: 32px;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 50;
  min-width: 160px;
  overflow: hidden;
`;

const DropItem = styled.button<{ danger?: boolean }>`
  display: block;
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.danger ? '#dc2626' : '#374151'};
  background: none;
  border: none;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 0 20px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
`;

const AIBanner = styled.div<{ priority: AIPriority | null }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: ${p =>
    p.priority === 'P1' ? '#fef2f2'
    : p.priority === 'P3' ? '#fefce8'
    : '#fffbeb'};
  border-bottom: 1px solid ${p =>
    p.priority === 'P1' ? '#fecaca'
    : p.priority === 'P3' ? '#fef08a'
    : '#fde68a'};
`;

const PriorityPill = styled.span<{ priority: AIPriority }>`
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 800;
  color: white;
  padding: 3px 9px;
  border-radius: 6px;
  background: ${p => PRIORITY_BG[p.priority]};
  white-space: nowrap;
  flex-shrink: 0;
`;

const BannerNote = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  color: #374151;
  margin: 0;
`;

const LostReasonBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  color: #991b1b;
  font-size: 13px;
  font-family: 'Urbanist', sans-serif;
`;

const Section = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
`;

const SectionLabel = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 8px;
`;

const EventTypeTxt = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const EventMeta = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  color: #6b7280;
  margin: 3px 0 0;
`;

const StageBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 4px 0;
  overflow-x: auto;
`;

const StageStep = styled.button<{ active: boolean; completed: boolean; valid: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 48px;
  background: none;
  border: none;
  cursor: ${p => p.valid ? 'pointer' : 'not-allowed'};
  padding: 4px 2px;
  opacity: ${p => (!p.active && !p.completed && !p.valid) ? 0.35 : 1};
`;

const StageCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 800;
  box-sizing: border-box;
  background: ${p =>
    (p.active || p.completed)
      ? 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8), rgba(206, 86, 176, 0.8))'
      : '#f3f4f6'};
  color: ${p => (p.active || p.completed) ? 'white' : '#9ca3af'};
  border: ${p => (p.active || p.completed) ? 'none' : '2px solid #e5e7eb'};
`;

const StageStepLabel = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 9px;
  color: #9ca3af;
  white-space: nowrap;
  max-width: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StageDivider = styled.div<{ completed: boolean }>`
  flex: 1;
  height: 2px;
  background: ${p => p.completed ? 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8), rgba(206, 86, 176, 0.8))' : '#e5e7eb'};
  min-width: 12px;
  margin-bottom: 14px;
`;

const SelectStageLabel = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 10px;
`;

const SelectStageRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StagePill = styled.button<{ active: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${p => p.active ? 'transparent' : '#e5e7eb'};
  background: ${p => p.active ? 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)' : 'white'};
  color: ${p => p.active ? 'white' : '#374151'};
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: ${p => p.active ? 700 : 600};
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #C94DAA;
  }
`;

const ContactRow = styled.div`
  display: flex;
  gap: 10px;
`;

const ContactBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f9fafb; }
`;

const PhoneNum = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 10px;
`;

const FollowUpCard = styled.div`
  background: #faf5ff;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid #e9d5ff;
`;

const FollowUpRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const FollowUpBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const FUBtn = styled.button<{ variant?: 'pause' | 'primary' }>`
  padding: 7px 14px;
  border-radius: 8px;
  border: ${p => p.variant === 'pause' ? '1.5px solid #e5e7eb' : 'none'};
  background: ${p => p.variant === 'pause' ? 'white' : 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8), rgba(206, 86, 176, 0.8))'};
  color: ${p => p.variant === 'pause' ? '#374151' : 'white'};
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const FollowUpMeta = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  color: #4b5563;
  margin: 4px 0 0;
  font-weight: 500;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fef3c7;
  border-radius: 8px;
  margin-top: 8px;
`;

const WarningText = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  color: #92400e;
  margin: 0;
`;

const WarningLink = styled.span`
  color: #C94DAA;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  margin-left: 4px;
`;

const NoteInput = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  resize: none;
  min-height: 64px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #C94DAA; }
  &::placeholder { color: #9ca3af; }
`;

const NoteSubmitBtn = styled.button`
  margin-top: 6px;
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8), rgba(206, 86, 176, 0.8));
  color: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LoadMoreBtn = styled.button`
  display: block;
  width: 100%;
  padding: 10px;
  text-align: center;
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  color: #C94DAA;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const BottomBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-top: 1px solid #f3f4f6;
  padding: 10px 16px;
  gap: 10px;
  flex-shrink: 0;
`;

const SendQuoteBtn = styled.button`
  flex: 1;
  padding: 13px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
  color: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const MoveStageBtn = styled.button`
  padding: 12px 18px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #f9fafb; }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WaIconBtn = styled.button`
  padding: 12px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { background: #f0fdf4; }
`;

const DeleteConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 16px;
`;

const DeleteConfirmBox = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  max-width: 340px;
  width: 100%;
  box-shadow: 0 12px 40px rgba(0,0,0,0.18);
`;

const DeleteTitle = styled.h3`
  font-family: 'Urbanist', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 8px;
`;

const DeleteBody = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 20px;
`;

const DeleteBtnRow = styled.div`
  display: flex;
  gap: 10px;
`;

const DeleteCancelBtn = styled.button`
  flex: 1;
  padding: 11px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
`;

const DeleteConfirmBtn = styled.button`
  flex: 1;
  padding: 11px;
  border-radius: 10px;
  border: none;
  background: #dc2626;
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: white;
  cursor: pointer;
`;

const LeadDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { whatsAppAccount, walletBalance } = useWhatsApp();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("leads.update");
  const canDelete = hasPermission("leads.delete");

  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<LeadActivity[]>([]);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesHasMore, setActivitiesHasMore] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [markLostModal, setMarkLostModal] = useState(false);
  const [markLostLoading, setMarkLostLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [note, setNote] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [stageUpdating, setStageUpdating] = useState(false);

  const [fuLoading, setFuLoading] = useState(false);
  const [fuPauseLoading, setFuPauseLoading] = useState(false);
  const [fuResult, setFuResult] = useState<string>('');

  const { user, studio_logo, business } = useAppSelector((state: any) => state.auth);

  
  const loadLead = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getLeadDetailAPI(id);
      setLead(data);
      setActivities(data.activities || []);
    } catch {
      showErrorToast('Could not load lead');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  
  const {
    sharingLeadId,
    activeQuotation,
    activeOrder,
    tempQuotation,
    tempOrder,
    quotationsSelectionList,
    ordersSelectionList,
    noQuotationModal,
    setNoQuotationModal,
    noOrderModal,
    setNoOrderModal,
    showQuotationSelectModal,
    setShowQuotationSelectModal,
    showOrderSelectModal,
    setShowOrderSelectModal,
    showQuotationVerifyModal,
    setShowQuotationVerifyModal,
    showOrderVerifyModal,
    setShowOrderVerifyModal,
    handleSendQuotation,
    handleSendOrder,
    handleConfirmSendQuotation,
    handleCancelSendQuotation,
    handleConfirmSendOrder,
    handleCancelSendOrder,
    handleSelectQuotation,
    handleSelectOrder,
    setTempQuotation,
    setTempOrder,
    setSharingLeadId,
    printRef,
    orderPrintRef,
  } = useDocumentSharing(() => loadLead());

  const loadMoreActivities = async () => {
    if (!id) return;
    setActivitiesLoading(true);
    try {
      const nextPage = activitiesPage + 1;
      const res = await listLeadActivitiesAPI(id, nextPage);
      setActivities(prev => [...prev, ...res.results]);
      setActivitiesPage(nextPage);
      setActivitiesHasMore(!!res.next);
    } finally {
      setActivitiesLoading(false);
    }
  };

  
  const handleStageClick = (stage: LeadStage) => {
    if (!canUpdate) return;
    if (!lead || stageUpdating) return;
    if (stage === lead.stage) return;
    if (!VALID_TRANSITIONS[lead.stage].includes(stage)) return;
    if (stage === 'LOST') {
      setMarkLostModal(true);
      return;
    }
    setStageUpdating(true);
    updateLeadStageAPI(lead.id, { stage })
      .then(updated => {
        setLead(updated);
        showSuccessToast(`Stage updated to ${STAGE_LABELS[stage]}`);
      })
      .catch(err => showErrorToast(err?.response?.data?.detail || 'Stage update failed'))
      .finally(() => setStageUpdating(false));
  };

  const handleMarkLostConfirm = async (reason: string) => {
    if (!lead) return;
    setMarkLostLoading(true);
    try {
      const updated = await updateLeadStageAPI(lead.id, { stage: 'LOST', lost_reason: reason });
      setLead(updated);
      showSuccessToast('Lead marked as lost');
      setMarkLostModal(false);
    } catch (err: any) {
      showErrorToast(err?.response?.data?.detail || 'Failed');
    } finally {
      setMarkLostLoading(false);
    }
  };

  
  const handleSubmitNote = async () => {
    if (!lead || !note.trim()) return;
    setNoteSubmitting(true);
    try {
      const activity = await logLeadNoteAPI(lead.id, note.trim());
      setActivities(prev => [activity, ...prev]);
      setNote('');
      showSuccessToast('Note added');
    } catch {
      showErrorToast('Failed to add note');
    } finally {
      setNoteSubmitting(false);
    }
  };

  
  const isWaConnected = whatsAppAccount?.connected === true;
  const walletBal = parseFloat(walletBalance?.data?.balance || '0');
  const MIN_WALLET = 0.80;

  const handleTriggerFollowUp = async () => {
    if (!lead) return;
    setFuLoading(true);
    setFuResult('');
    try {
      const res = await triggerFollowUpAPI(lead.id);
      setFuResult(`Scheduled for ${formatScheduledAt(res.scheduled_at)} · Touch #${res.touch_number} · ₹${res.estimated_wallet_charge_inr}`);
      showSuccessToast('Follow-up scheduled!');
      loadLead();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.detail || 'Failed to schedule follow-up');
    } finally {
      setFuLoading(false);
    }
  };

  const handlePauseFollowUp = async () => {
    if (!lead) return;
    setFuPauseLoading(true);
    try {
      const res = await pauseFollowUpAPI(lead.id);
      showSuccessToast(`Follow-up paused (${res.cancelled_jobs} job${res.cancelled_jobs !== 1 ? 's' : ''} cancelled)`);
      loadLead();
    } catch {
      showErrorToast('Failed to pause follow-up');
    } finally {
      setFuPauseLoading(false);
    }
  };

  const handleResume = async () => {
    if (!lead) return;
    try {
      await updateLeadAPI(lead.id, { follow_up_paused: false });
      await handleTriggerFollowUp();
    } catch {
      showErrorToast('Failed to resume follow-up');
    }
  };

  
  const handleDelete = async () => {
    if (!lead) return;
    setDeleting(true);
    try {
      await deleteLeadAPI(lead.id);
      showSuccessToast('Lead deleted');
      navigate('/dashboard/leads');
    } catch {
      showErrorToast('Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const nextValidStages = lead ? VALID_TRANSITIONS[lead.stage].filter(s => s !== 'LOST') : [];

  if (loading) {
    return (
      <Page>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader size={28} color="#C94DAA" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </Page>
    );
  }

  if (!lead) {
    return (
      <Page>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontFamily: 'Urbanist', color: '#9ca3af' }}>Lead not found</p>
        </div>
      </Page>
    );
  }

  const stageIndex = STAGE_ORDER.indexOf(lead.stage);
  const isTerminal = lead.stage === 'BOOKED' || lead.stage === 'LOST';

  return (
    <Page>
      {/* Top Bar */}
      <TopBar>
        <BackBtn id="lead-detail-back-btn" onClick={() => navigate('/dashboard/leads')} aria-label="Go back">
          <img src={BackButtonIconPng} style={{ width: "19.6px", height: "16.8px" }} alt="Back" />
        </BackBtn>
        <LeadName>{lead.name}</LeadName>
        {(canUpdate || canDelete) && (
          <MenuBtn onClick={() => setMenuOpen(m => !m)}>
            <MoreVertical size={20} />
            <DropMenu open={menuOpen}>
              {canUpdate && (
                <DropItem onClick={() => { setMenuOpen(false); setEditModalOpen(true); }}>
                  Edit Lead
                </DropItem>
              )}
              {canDelete && (
                <DropItem danger onClick={() => { setMenuOpen(false); setDeleteConfirm(true); }}>
                  Delete Lead
                </DropItem>
              )}
            </DropMenu>
          </MenuBtn>
        )}
      </TopBar>

      <Body onClick={() => setMenuOpen(false)}>
        {/* AI Priority Banner */}
        {lead.ai_priority && (
          <AIBanner priority={lead.ai_priority}>
            <PriorityPill priority={lead.ai_priority}>{lead.ai_priority} ★</PriorityPill>
            <BannerNote>{lead.notes || 'No notes yet.'}</BannerNote>
          </AIBanner>
        )}

        {/* Lost Reason Banner */}
        {lead.stage === 'LOST' && lead.lost_reason && (
          <LostReasonBanner>
            <strong style={{ color: '#dc2626' }}>LOST REASON:</strong>
            <span>{lead.lost_reason}</span>
          </LostReasonBanner>
        )}

        {/* Event Section */}
        <Section>
          <SectionLabel>Event</SectionLabel>
          <EventTypeTxt>{EVENT_TYPE_LABELS[lead.event_type] ?? lead.event_type}</EventTypeTxt>
          {lead.event_date && <EventMeta>{formatEventDate(lead.event_date)}</EventMeta>}
          {lead.venue_city && <EventMeta>{lead.venue_city}</EventMeta>}
          {(lead.budget_min || lead.budget_max) && (
            <EventMeta style={{ fontWeight: 600, color: '#374151' }}>
              {formatBudget(lead.budget_min, lead.budget_max)}
            </EventMeta>
          )}
        </Section>

        {/* Stage Progress Bar */}
        <Section>
          <StageBar>
            {STAGE_ORDER.map((stage, idx) => {
              const active = stage === lead.stage;
              const completed = idx < stageIndex;
              const valid = VALID_TRANSITIONS[lead.stage].includes(stage);

              return (
                <React.Fragment key={stage}>
                  <StageStep
                    id={`stage-step-${stage}`}
                    active={active}
                    completed={completed}
                    valid={valid && canUpdate}
                    onClick={() => canUpdate && handleStageClick(stage)}
                    title={valid && canUpdate ? `Move to ${STAGE_LABELS[stage]}` : ''}
                  >
                    <StageCircle active={active} completed={completed}>
                      {completed ? <Check size={14} color="white" strokeWidth={3} /> : idx + 1}
                    </StageCircle>
                    <StageStepLabel>{STAGE_LABELS[stage].split(' ')[0].slice(0, 7)}</StageStepLabel>
                  </StageStep>
                  {idx < STAGE_ORDER.length - 1 && (
                    <StageDivider completed={idx < stageIndex} />
                  )}
                </React.Fragment>
              );
            })}
          </StageBar>
        </Section>

        {/* Contact Section */}
        <Section>
          <SectionLabel>Contact</SectionLabel>
          <PhoneNum>{formatPhoneNumber(lead.phone, lead.country_code)}</PhoneNum>
          <ContactRow>
            <ContactBtn href={`tel:${getFullPhone(lead.phone, lead.country_code)}`}>
              <Phone size={15} color="#374151" />
              Call
            </ContactBtn>
            <ContactBtn
              href={`https://wa.me/${getFullPhone(lead.phone, lead.country_code).replace('+', '')}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle size={15} color="#16a34a" />
              WhatsApp
            </ContactBtn>
          </ContactRow>
        </Section>

        {/* Follow-up Section */}
        {!isTerminal && (
          <Section>
            <SectionLabel>Follow-up</SectionLabel>
            <FollowUpCard>
              <FollowUpRow>
                <div>
                  <p style={{ fontFamily: 'Urbanist', fontSize: 13, fontWeight: 600, color: '#7c3aed', margin: 0 }}>
                    {lead.follow_up_count > 0
                      ? `Touch ${lead.follow_up_count} sent`
                      : 'No follow-up sent yet'}
                  </p>
                  {lead.next_follow_up_at && (
                    <FollowUpMeta>
                      Next: {formatScheduledAt(lead.next_follow_up_at)}
                    </FollowUpMeta>
                  )}
                  {fuResult && (
                    <FollowUpMeta style={{ color: '#16a34a' }}>{fuResult}</FollowUpMeta>
                  )}
                </div>
                {canUpdate && (
                  <FollowUpBtns>
                    {lead.follow_up_paused ? (
                      <FUBtn onClick={handleResume} disabled={fuLoading}>Resume</FUBtn>
                    ) : (
                      <>
                        <FUBtn onClick={handleTriggerFollowUp} disabled={fuLoading || !isWaConnected || walletBal < MIN_WALLET}>
                          {fuLoading ? 'Sending…' : 'Trigger'}
                        </FUBtn>
                        {lead.follow_up_count > 0 && (
                          <FUBtn variant="pause" onClick={handlePauseFollowUp} disabled={fuPauseLoading}>
                            {fuPauseLoading ? '…' : 'Pause'}
                          </FUBtn>
                        )}
                      </>
                    )}
                  </FollowUpBtns>
                )}
              </FollowUpRow>

              {/* Warnings */}
              {!isWaConnected && (
                <WarningBanner>
                  <span>⚠</span>
                  <WarningText>
                    WhatsApp not connected.
                    <WarningLink onClick={() => navigate('/dashboard/whatsapp-setup')}>
                      Connect WhatsApp →
                    </WarningLink>
                  </WarningText>
                </WarningBanner>
              )}

              {isWaConnected && walletBal < MIN_WALLET && (
                <WarningBanner>
                  <span>⚠</span>
                  <WarningText>
                    Insufficient wallet balance (₹{walletBal.toFixed(2)}). Min ₹{MIN_WALLET} required.
                    <WarningLink onClick={() => navigate('/dashboard/whatsapp/billing')}>
                      Recharge Wallet →
                    </WarningLink>
                  </WarningText>
                </WarningBanner>
              )}

              <p style={{ fontFamily: 'Urbanist', fontSize: 11, color: '#6b7280', margin: '6px 0 0', fontWeight: 500 }}>
                Wallet: ₹{walletBal.toFixed(2)} · Est. ₹0.15/message
              </p>
            </FollowUpCard>
          </Section>
        )}

        {/* Activity & Notes Section */}
        <Section>
          <SectionLabel>Notes & Activity</SectionLabel>

          {canUpdate && (
            <>
              <NoteInput
                id="lead-note-input"
                placeholder="Add a note…"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
              {note.trim() && (
                <NoteSubmitBtn
                  id="lead-note-submit"
                  onClick={handleSubmitNote}
                  disabled={noteSubmitting}
                >
                  {noteSubmitting ? 'Saving…' : 'Add Note'}
                </NoteSubmitBtn>
              )}
            </>
          )}

          <div style={{ marginTop: 16 }}>
            {activities.map(a => (
              <LeadActivityItem key={a.id} activity={a} />
            ))}
          </div>

          {activitiesHasMore && (
            <LoadMoreBtn onClick={loadMoreActivities} disabled={activitiesLoading}>
              {activitiesLoading ? 'Loading…' : 'Load more activity'}
            </LoadMoreBtn>
          )}
        </Section>

        {/* Stage Selector */}
        <Section style={{ borderBottom: 'none' }}>
          <SelectStageLabel>Select stage</SelectStageLabel>
          <SelectStageRow>
            {STAGE_ORDER.map(stage => {
              const active = stage === lead.stage;
              const valid = VALID_TRANSITIONS[lead.stage].includes(stage) || active;
              const isAllowed = canUpdate && valid;
              return (
                <StagePill
                  key={stage}
                  active={active}
                  disabled={!isAllowed && !active}
                  onClick={() => isAllowed && handleStageClick(stage)}
                  style={{ opacity: (!isAllowed && !active) ? 0.5 : 1, cursor: (!isAllowed && !active) ? 'not-allowed' : 'pointer' }}
                >
                  {STAGE_LABELS[stage]}
                </StagePill>
              );
            })}
          </SelectStageRow>
        </Section>
      </Body>

      {/* Bottom Bar */}
      <BottomBar>
        {lead.stage === 'CONTACTED' ? (
          <SendQuoteBtn
            id="send-quote-btn"
            disabled={sharingLeadId === lead.id}
            onClick={() => handleSendQuotation(lead)}
          >
            {sharingLeadId === lead.id ? 'Sending...' : 'Send Quotation'}
          </SendQuoteBtn>
        ) : lead.stage === 'ADVANCE_PAID' ? (
          <SendQuoteBtn
            id="send-order-btn"
            disabled={sharingLeadId === lead.id}
            onClick={() => handleSendOrder(lead)}
          >
            {sharingLeadId === lead.id ? 'Sending...' : 'Send Order'}
          </SendQuoteBtn>
        ) : null}

        {canUpdate && nextValidStages.length > 0 && (
          <MoveStageBtn
            id="move-stage-btn"
            disabled={stageUpdating}
            onClick={() => handleStageClick(nextValidStages[0])}
          >
            Move Stage
          </MoveStageBtn>
        )}
        <WaIconBtn
          id="open-wa-chat-btn"
          onClick={() => navigate(`/dashboard/whatsapp/conversations`)}
        >
          <MessageCircle size={20} color="#16a34a" />
        </WaIconBtn>
      </BottomBar>

      <MarkLostModal
        open={markLostModal}
        onClose={() => setMarkLostModal(false)}
        onConfirm={handleMarkLostConfirm}
        loading={markLostLoading}
      />

      <AddLeadModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        leadToEdit={lead}
        onSuccess={() => loadLead()}
      />

      {/* Delete Confirmation Overlay */}
      {deleteConfirm && (
        <DeleteConfirmOverlay>
          <DeleteConfirmBox>
            <DeleteTitle>Delete this lead?</DeleteTitle>
            <DeleteBody>
              This will mark the lead as Lost and cannot be undone.
            </DeleteBody>
            <DeleteBtnRow>
              <DeleteCancelBtn onClick={() => setDeleteConfirm(false)}>Cancel</DeleteCancelBtn>
              <DeleteConfirmBtn onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </DeleteConfirmBtn>
            </DeleteBtnRow>
          </DeleteConfirmBox>
        </DeleteConfirmOverlay>
      )}

      {/* Sharing Modals */}
      <NoDocumentModal
        open={noQuotationModal.open}
        lead={noQuotationModal.lead}
        type="quotation"
        onClose={() => setNoQuotationModal({ open: false, lead: null })}
        onConfirm={() => {
          const leadData = noQuotationModal.lead;
          setNoQuotationModal({ open: false, lead: null });
          navigate('/dashboard/quotation/singleQuotation', { state: { leadData } });
        }}
      />

      <NoDocumentModal
        open={noOrderModal.open}
        lead={noOrderModal.lead}
        type="order"
        onClose={() => setNoOrderModal({ open: false, lead: null })}
        onConfirm={() => {
          const leadData = noOrderModal.lead;
          setNoOrderModal({ open: false, lead: null });
          navigate('/dashboard/orders/create', { state: { leadData } });
        }}
      />

      <VerifyDocumentModal
        open={showQuotationVerifyModal}
        tempDoc={tempQuotation}
        type="quotation"
        onClose={handleCancelSendQuotation}
        onConfirm={handleConfirmSendQuotation}
      />

      <VerifyDocumentModal
        open={showOrderVerifyModal}
        tempDoc={tempOrder}
        type="order"
        onClose={handleCancelSendOrder}
        onConfirm={handleConfirmSendOrder}
      />

      <SelectDocumentModal
        open={showQuotationSelectModal}
        list={quotationsSelectionList}
        type="quotation"
        onClose={() => { setShowQuotationSelectModal(false); setSharingLeadId(null); }}
        onSelect={handleSelectQuotation}
      />

      <SelectDocumentModal
        open={showOrderSelectModal}
        list={ordersSelectionList}
        type="order"
        onClose={() => { setShowOrderSelectModal(false); setSharingLeadId(null); }}
        onSelect={handleSelectOrder}
      />

      {/* Hidden Print Templates */}
      <DocumentPrintTemplate
        printRef={printRef}
        activeDoc={activeQuotation}
        user={user}
        studioLogo={studio_logo || business?.studio_logo}
        studioName={business?.studio_name}
        studioAddress={business?.address}
        type="quotation"
      />

      <DocumentPrintTemplate
        printRef={orderPrintRef}
        activeDoc={activeOrder}
        user={user}
        studioLogo={studio_logo || business?.studio_logo}
        studioName={business?.studio_name}
        studioAddress={business?.address}
        type="order"
      />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Page>
  );
};

export default LeadDetailsPage;
